<template>
  <Teleport to="#app">
    <Transition name="toast">
      <div v-if="visible" class="transaction-toast mt-3" :class="toastClass">
        <!-- Confetti Canvas inside toast -->
        <canvas v-if="showConfetti" ref="confettiCanvas" class="confetti-canvas"></canvas>

        <div class="toast-icon" :class="{ 'icon-celebrate': showCelebration }">
          <i :class="iconClass"></i>
        </div>
        <div class="toast-content">
          <div class="toast-title">{{ title }}</div>
          <div v-if="encouragingMessage" class="toast-encouragement">
            {{ encouragingMessage }}
          </div>
          <div class="toast-details">
            <span v-if="amount > 0" class="toast-amount">{{ formattedAmount }}</span>
            <span v-if="description" class="toast-description">{{ description }}</span>
            <span v-if="autoCategory" class="toast-category">
              <i class="bi bi-tag-fill me-1"></i>Auto-categorized as "{{ autoCategory }}"
            </span>
            <span v-if="learnedKeywords" class="toast-learned">
              <i class="bi bi-lightbulb-fill me-1"></i>Learned {{ learnedKeywords.count }} new keyword{{
                learnedKeywords.count > 1 ? 's' : '' }}
            </span>
            <span v-if="remainingToSpend !== null" class="toast-remaining">
              <i class="bi bi-wallet2 me-1"></i>{{ formattedRemaining }} left to spend today
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useConfetti } from '../composables/useConfetti';

// State
const visible = ref(false);
const type = ref('debit');
const amount = ref(0);
const description = ref('');
const autoCategory = ref('');
const learnedKeywords = ref(null);
const remainingToSpend = ref(null);
const customTitle = ref(null);
const timeout = ref(null);
const isLoading = ref(false);
const transactionCount = ref(1);
const showCelebration = ref(false);
const encouragingMessage = ref('');

// Canvas ref for confetti
const confettiCanvas = ref(null);

// Confetti composable
const { showConfetti, startConfetti, stopConfetti } = useConfetti(confettiCanvas);

// Computed properties
const toastClass = computed(() => {
  if (type.value === 'debit') return 'toast-debit';
  if (type.value === 'credit') return 'toast-credit';
  if (type.value === 'savings') return 'toast-savings';
  return 'toast-debit';
});

const iconClass = computed(() => {
  if (isLoading.value) return 'bi bi-arrow-repeat spinner-icon';
  if (type.value === 'debit') return 'bi bi-arrow-down-circle-fill';
  if (type.value === 'credit') return 'bi bi-arrow-up-circle-fill';
  if (type.value === 'savings') return 'bi bi-piggy-bank-fill';
  return 'bi bi-arrow-down-circle-fill';
});

const title = computed(() => {
  if (customTitle.value) return customTitle.value;
  if (isLoading.value) {
    return transactionCount.value > 1
      ? `Adding ${transactionCount.value} Transactions...`
      : 'Adding Transaction...';
  }
  if (type.value === 'debit') return 'Expense Added';
  if (type.value === 'credit') return 'Income Added';
  if (type.value === 'savings') return 'Savings Contribution';
  return 'Transaction Added';
});

const formattedAmount = computed(() => {
  return `$${Math.abs(amount.value).toFixed(2)}`;
});

const formattedRemaining = computed(() => {
  if (remainingToSpend.value === null) return '';
  return `$${remainingToSpend.value.toFixed(2)}`;
});

// Methods
const getEncouragingMessage = (txType, txAmount, remaining) => {
  const messages = {
    debit: [
      '💪 Tracked!',
      '✨ Logged!',
      '📝 Noted!',
      '👍 Got it!',
      '✅ Recorded!'
    ],
    credit: [
      '🎉 Nice income!',
      '💰 Money in!',
      '🌟 Great addition!',
      '💸 Cha-ching!',
      '🚀 Boosted!'
    ],
    savings: [
      '🎯 Saving strong!',
      '💎 Building wealth!',
      '🌱 Growing your future!',
      '⭐ Smart move!',
      '🏆 Savings champion!'
    ]
  };

  // Special messages for large amounts
  if (txType === 'credit' && txAmount > 1000) {
    return '🎊 Big win! Keep it up!';
  }
  if (txType === 'savings' && txAmount > 500) {
    return '🔥 Crushing your goals!';
  }

  // Special message if staying within budget
  if (txType === 'debit' && remaining !== null && remaining > 0) {
    const onTrackMessages = [
      '💚 Still on track!',
      '✅ Within budget!',
      '👌 Looking good!',
      '🎯 On target!',
      '💪 Staying strong!',
      '🌟 Budget friendly!',
      '✨ Nice control!'
    ];
    return onTrackMessages[Math.floor(Math.random() * onTrackMessages.length)];
  }

  const messageList = messages[txType] || messages.debit;
  return messageList[Math.floor(Math.random() * messageList.length)];
};

const triggerCelebration = () => {
  // Show celebration animation
  showCelebration.value = true;
  setTimeout(() => {
    showCelebration.value = false;
  }, 2500);

  startConfetti();
};

const show = (txType, txAmount, txDescription = '', remaining = null, category = '', keywords = null, title = null) => {
  type.value = txType;
  amount.value = txAmount;
  description.value = txDescription;
  autoCategory.value = category;
  learnedKeywords.value = keywords;
  remainingToSpend.value = remaining;
  customTitle.value = title;
  isLoading.value = false;
  encouragingMessage.value = getEncouragingMessage(txType, txAmount, remaining);
  visible.value = true;

  // Trigger celebration effects
  triggerCelebration();

  if (timeout.value) {
    clearTimeout(timeout.value);
  }

  timeout.value = setTimeout(() => {
    visible.value = false;
    encouragingMessage.value = '';
    stopConfetti();
  }, 4000);
};

const showLoading = (count = 1, firstTransaction = null) => {
  isLoading.value = true;
  transactionCount.value = count;
  type.value = firstTransaction?.type || 'debit';
  amount.value = firstTransaction?.amount || 0;
  description.value = firstTransaction?.description || '';
  autoCategory.value = '';
  learnedKeywords.value = null;
  remainingToSpend.value = null;
  customTitle.value = null;
  visible.value = true;

  if (timeout.value) {
    clearTimeout(timeout.value);
  }
};

const complete = (txType, txAmount, txDescription = '', remaining = null, category = '', keywords = null) => {
  isLoading.value = false;
  type.value = txType;
  amount.value = txAmount;
  description.value = txDescription;
  autoCategory.value = category;
  learnedKeywords.value = keywords;
  remainingToSpend.value = remaining;
  encouragingMessage.value = getEncouragingMessage(txType, txAmount, remaining);

  if (timeout.value) {
    clearTimeout(timeout.value);
  }

  timeout.value = setTimeout(() => {
    visible.value = false;
    encouragingMessage.value = '';
    stopConfetti();
  }, 3000);
};

const hide = () => {
  if (timeout.value) {
    clearTimeout(timeout.value);
  }
  visible.value = false;
  isLoading.value = false;
  encouragingMessage.value = '';
  stopConfetti();
};

// Cleanup
onBeforeUnmount(() => {
  if (timeout.value) {
    clearTimeout(timeout.value);
  }
  stopConfetti();
});

// Expose public methods for parent components
defineExpose({
  show,
  showLoading,
  complete,
  hide
});
</script>

<style scoped>
.transaction-toast {
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: var(--light-card);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  max-width: 400px;
  border-left: 5px solid;
  overflow: hidden;
  /* iOS Safari fixes for position fixed */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.confetti-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  display: block;
}

.toast-icon,
.toast-content {
  position: relative;
  z-index: 2;
}

.toast-debit {
  border-left-color: var(--light-danger);
}

.toast-credit {
  border-left-color: var(--light-success);
}

.toast-savings {
  border-left-color: #36A2EB;
}

.toast-icon {
  font-size: 24px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.toast-icon.icon-celebrate {
  animation: celebrate 0.6s ease;
}

@keyframes celebrate {

  0%,
  100% {
    transform: scale(1) rotate(0deg);
  }

  25% {
    transform: scale(1.3) rotate(-10deg);
  }

  50% {
    transform: scale(1.3) rotate(10deg);
  }

  75% {
    transform: scale(1.3) rotate(-10deg);
  }
}

.toast-icon .spinner-icon {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.toast-debit .toast-icon {
  color: var(--light-danger);
}

.toast-credit .toast-icon {
  color: var(--light-success);
}

.toast-savings .toast-icon {
  color: #36A2EB;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--light-text);
  margin-bottom: 2px;
}

.toast-encouragement {
  font-size: 13px;
  font-weight: 600;
  color: var(--light-primary);
  margin-bottom: 4px;
  animation: fadeInUp 0.4s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toast-amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--light-text);
}

.toast-description {
  font-size: 13px;
  color: var(--light-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-category {
  font-size: 12px;
  color: var(--light-primary);
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.toast-learned {
  font-size: 12px;
  color: var(--light-success);
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.toast-remaining {
  font-size: 12px;
  color: var(--light-text-muted);
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-top: 4px;
}

/* Animation */
.toast-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-out 0.3s ease-in;
}

@keyframes toast-in {
  from {
    transform: translateX(calc(100% + 20px));
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes toast-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(calc(100% + 20px));
    opacity: 0;
  }
}

/* Additional iOS Safari fixes */
@supports (-webkit-touch-callout: none) {
  .transaction-toast {
    /* Force hardware acceleration on iOS */
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
  }
}

/* Dark mode support */
:global([data-theme="dark"]) .transaction-toast {
  background: var(--light-card);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

:global([data-theme="dark"]) .toast-debit {
  border-left-color: var(--light-danger);
}

:global([data-theme="dark"]) .toast-credit {
  border-left-color: var(--light-success);
}

:global([data-theme="dark"]) .toast-savings {
  border-left-color: #36A2EB;
}

:global([data-theme="dark"]) .toast-debit .toast-icon {
  color: var(--light-danger);
}

:global([data-theme="dark"]) .toast-credit .toast-icon {
  color: var(--light-success);
}

:global([data-theme="dark"]) .toast-savings .toast-icon {
  color: #36A2EB;
}

:global([data-theme="dark"]) .toast-title {
  color: var(--light-text);
}

:global([data-theme="dark"]) .toast-amount {
  color: var(--light-text);
}

:global([data-theme="dark"]) .toast-description {
  color: var(--light-text-muted);
}

:global([data-theme="dark"]) .toast-category {
  color: var(--light-primary);
}

:global([data-theme="dark"]) .toast-remaining {
  color: var(--light-text-muted);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .transaction-toast {
    top: 80px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
}
</style>
